import { MenuOutlined } from '@ant-design/icons'
import {
  type SequenceReadable,
  sequencesRetrieveOptions,
  sequencesStepsDestroyMutation,
  sequencesStepsListOptions,
  sequencesStepsListQueryKey,
  sequencesStepsPartialUpdateMutation
} from '@crm/api'
import { ConfirmModal, DndZone } from '@crm/components'
import {
  CampaignLeads,
  StepsCard,
  StepsForm
} from '@crm/features/campaigns/components'
import { cardType, stats, textType } from '@crm/features/campaigns/utils'
import { TitleSection } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  type AlertProps,
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Modal,
  notification,
  Row,
  Space,
  Typography
} from 'antd'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { useStyles } from './styles'

import type { BaseType } from 'antd/es/typography/Base'

const { Text } = Typography

const formId = 'form-steps'

export const Steps = () => {
  const { t } = useLocales()
  const { id: campaignId } = useParams()
  const { styles } = useStyles()

  const { invalidateQueries } = useInvalidatePredicateQueries()

  const [isCreate, setIsCreate] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()

  const { data: campaign } = useQuery({
    ...sequencesRetrieveOptions({ path: { id: Number(campaignId) } }),
    enabled: !!campaignId
  })

  const { data: steps, isLoading } = useQuery({
    ...sequencesStepsListOptions({ path: { sequence_id: String(campaignId) } }),
    enabled: !!campaignId,
    select: (steps) => steps.results
  })

  const onOpenModal = () => setIsCreate(true)

  const { mutate, isPending: isDeletePending } = useMutation({
    ...sequencesStepsDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([
        sequencesStepsListQueryKey({
          path: { sequence_id: String(campaignId) }
        })
      ])
      setDeleteItem(undefined)

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const { mutate: mutateStepPosition, isPending: isEditPending } = useMutation({
    ...sequencesStepsPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () =>
      invalidateQueries([
        sequencesStepsListQueryKey({
          path: { sequence_id: String(campaignId) }
        })
      ])
  })

  const onDelete = () => {
    if (deleteItem) {
      mutate({ path: { sequence_id: String(campaignId), id: deleteItem } })
    }
  }

  const onCancelDelete = () => setDeleteItem(undefined)

  const onCloseModal = () => {
    setEditItem(undefined)
    setIsCreate(false)
  }

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    if (steps && oldIndex !== newIndex) {
      const stepId = steps?.find((_, i: number) => i === oldIndex)?.id

      mutateStepPosition({
        path: { sequence_id: String(campaignId), id: Number(stepId) },
        body: { number: newIndex + 1 }
      })
    }
  }

  return (
    <>
      <TitleSection title={t(`menu.campaigns`)} />

      <Row gutter={[18, 18]} className="mb-20">
        {campaign &&
          stats.map((status, index) => (
            <Col span={24} sm={12} xl={6} key={`${status}--${index}`}>
              <Alert
                type={
                  cardType[
                    status as keyof typeof cardType
                  ] as AlertProps['type']
                }
                message={
                  <Flex justify="center">
                    <Text
                      type={
                        textType[status as keyof typeof textType] as BaseType
                      }
                    >
                      <Space align="center" direction="vertical" size={0}>
                        {
                          campaign[
                            status as keyof Pick<
                              SequenceReadable,
                              | 'stats_leads'
                              | 'stats_active'
                              | 'stats_failed'
                              | 'stats_success'
                            >
                          ]
                        }
                        {t(`campaigns.${status}`)}
                      </Space>
                    </Text>
                  </Flex>
                }
              />
            </Col>
          ))}
      </Row>

      <Row gutter={[18, 18]}>
        <Col lg={24} xl={14}>
          <DndZone
            items={steps || []}
            getSortableItem={(_, i) => i}
            onDragEnd={(_, { active, over }) =>
              onSortEnd(active?.id as number, over?.id as number)
            }
          >
            <Card loading={isLoading} className={styles.stepsList}>
              <Flex gap={16} vertical>
                {!!steps?.length &&
                  steps?.map((step, i: number) => (
                    <DndZone.ZoneItem key={step.id} id={i}>
                      {({ listeners, setActivatorNodeRef }) => (
                        <StepsCard
                          step={step}
                          dragHandler={
                            <MenuOutlined
                              ref={setActivatorNodeRef}
                              style={{ cursor: 'grab' }}
                              {...listeners}
                            />
                          }
                          onEdit={setEditItem}
                          onDelete={setDeleteItem}
                        />
                      )}
                    </DndZone.ZoneItem>
                  ))}

                <Flex justify="center">
                  <Button type="primary" onClick={onOpenModal}>
                    {t('campaigns.add_new_step')}
                  </Button>
                </Flex>
              </Flex>
            </Card>
          </DndZone>
        </Col>

        <Col lg={24} xl={10}>
          <CampaignLeads />
        </Col>
      </Row>

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <Modal
        width={800}
        maskClosable={false}
        open={!!editItem || isCreate}
        destroyOnHidden
        title={t(isCreate ? 'campaigns.add_step' : 'campaigns.edit_step')}
        onCancel={onCloseModal}
        cancelText={t('buttons.cancel')}
        okButtonProps={{
          htmlType: 'submit',
          form: formId,
          loading: isDeletePending || isEditPending
        }}
        okText={t(isCreate ? 'buttons.add' : 'buttons.edit')}
      >
        <StepsForm formId={formId} id={editItem} onCancel={onCloseModal} />
      </Modal>
    </>
  )
}
